import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import BackToTop from "@/components/feature/BackToTop";
import JsonLd from "@/components/feature/JsonLd";
import PageSeo from "@/components/feature/PageSeo";
import FaqSection from "@/pages/home/components/FaqSection";
import { siteInfo, stats, testimonials } from "@/mocks/gossip";

const positioning = [
  {
    icon: "ri-focus-3-line",
    title: "只做画面整理",
    desc: "不生产剧情、不站队评判，把多渠道流传的视频、图片与截图归拢到一处，标注时间与来源。",
  },
  {
    icon: "ri-time-line",
    title: "强调时间线",
    desc: "热点往往一晚上反转多次。我们按时间顺序梳理关键节点，让读者自己看清来龙去脉。",
  },
  {
    icon: "ri-shield-check-line",
    title: "先核验再发布",
    desc: "对每条收录内容做基础真实性核验，涉及隐私的画面会做模糊处理，减少二次伤害。",
  },
];

const principles = [
  { title: "来源可追溯", desc: "每条瓜料都尽可能标注首次出现的时间与渠道，方便读者自行比对。" },
  { title: "拒绝标题党", desc: "标题与画面内容保持一致，不做脱离事实的夸张渲染。" },
  { title: "尊重当事人", desc: "不曝光与事件无关的个人信息，涉及未成年人内容一律不予收录。" },
  { title: "及时更正", desc: "若内容核实有误，会在原页面标注更正说明，而不是悄悄删除。" },
];

export default function AboutPage() {
  const aboutLd = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    name: `关于我们 - ${siteInfo.brand}`,
    description: siteInfo.intro,
    inLanguage: "zh-CN",
  };

  const breadcrumbLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "首页", item: "https://hebeishifenhao.cn/" },
      {
        "@type": "ListItem",
        position: 2,
        name: "关于我们",
        item: "https://hebeishifenhao.cn/about",
      },
    ],
  };

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <PageSeo
        title="关于我们 - 瓜田前线网曝吃瓜视觉合集"
        description="了解瓜田前线的网站定位、内容原则与发展理念：专注整理网曝吃瓜视频、图片与实拍画面，坚持先核验再发布，尊重当事人合法权益。"
        keywords="关于瓜田前线,内容原则,网曝吃瓜,吃瓜合集,内容核验"
        canonical="https://hebeishifenhao.cn/about"
      />
      <JsonLd data={aboutLd} />
      <JsonLd data={breadcrumbLd} />
      <Navbar />

      <main className="flex-1 w-full">
        <section className="w-full bg-background-100 border-b border-background-200">
          <div className="w-full px-4 md:px-8 py-12 md:py-16">
            <nav aria-label="面包屑" className="flex items-center gap-2 text-xs text-foreground-500 mb-6">
              <Link to="/" className="hover:text-primary-600 transition-colors">
                首页
              </Link>
              <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
              <span className="text-foreground-800">关于我们</span>
            </nav>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
              <div className="lg:col-span-6">
                <p className="inline-flex items-center gap-2 text-xs font-label tracking-[0.2em] uppercase text-primary-600 mb-3">
                  <span className="w-6 h-px bg-primary-500 inline-block"></span>
                  About Us
                </p>
                <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950 leading-tight">
                  关于{siteInfo.brand}
                </h1>
                <p className="mt-5 text-sm md:text-base text-foreground-700 leading-loose">
                  {siteInfo.intro}
                </p>
                <p className="mt-4 text-sm md:text-base text-foreground-600 leading-loose">
                  我们相信，热点资讯的价值不在于谁的声音更大，而在于信息是否完整、时间线是否清楚。
                  因此{siteInfo.brand}把大部分精力放在整理与核对上，而不是追逐流量情绪。
                </p>
                <div className="mt-7 flex flex-wrap gap-3">
                  <Link
                    to="/category"
                    className="px-5 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
                  >
                    浏览全部瓜料
                  </Link>
                  <a
                    href="#subscribe"
                    className="px-5 py-3 rounded-md border border-background-300 text-foreground-800 text-sm font-medium hover:border-primary-300 hover:text-primary-600 transition-colors whitespace-nowrap"
                  >
                    订阅更新提醒
                  </a>
                </div>
              </div>
              <div className="lg:col-span-6">
                <div className="w-full aspect-[16/11] rounded-lg overflow-hidden border border-background-200 bg-background-200">
                  <img
                    src="https://readdy.ai/api/search-image?query=Warm%20minimal%20editorial%20desk%20scene%20with%20soft%20amber%20light%2C%20abstract%20screens%20and%20paper%20notes%2C%20cozy%20studio%20atmosphere%2C%20clean%20modern%20photography%2C%20terracotta%20and%20cream%20palette%2C%20no%20readable%20text%2C%20no%20people&width=1400&height=960&seq=about-gua-main&orientation=landscape"
                    alt="瓜田前线内容整理工作场景示意"
                    title="瓜田前线 - 内容整理与核验"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-8 py-14 md:py-20">
          <div className="max-w-2xl">
            <p className="inline-flex items-center gap-2 text-xs font-label tracking-[0.2em] uppercase text-primary-600 mb-3">
              <span className="w-6 h-px bg-primary-500 inline-block"></span>
              Positioning
            </p>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">网站定位</h2>
            <p className="mt-3 text-sm md:text-base text-foreground-600 leading-relaxed">
              我们不做全网最快，但希望成为最清楚的那个。三条定位贯穿所有内容的整理方式。
            </p>
          </div>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-5">
            {positioning.map((item) => (
              <div key={item.title} className="p-6 rounded-lg bg-background-50 border border-background-200">
                <span className="w-12 h-12 rounded-lg bg-primary-100 text-primary-700 flex items-center justify-center text-2xl">
                  <i className={item.icon} aria-hidden="true"></i>
                </span>
                <h3 className="mt-5 font-heading text-lg font-semibold text-foreground-900">{item.title}</h3>
                <p className="mt-2.5 text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="w-full bg-background-100 border-y border-background-200">
          <div className="w-full px-4 md:px-8 py-12 md:py-16">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
              {stats.map((stat) => (
                <div key={stat.label} className="text-center">
                  <span className="mx-auto w-11 h-11 rounded-lg bg-secondary-100 text-secondary-700 flex items-center justify-center text-xl">
                    <i className={stat.icon} aria-hidden="true"></i>
                  </span>
                  <p className="mt-4 font-heading text-2xl md:text-3xl font-bold text-foreground-950">
                    {stat.value}
                  </p>
                  <p className="mt-1 text-sm text-foreground-600">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-8 py-14 md:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5">
              <p className="inline-flex items-center gap-2 text-xs font-label tracking-[0.2em] uppercase text-primary-600 mb-3">
                <span className="w-6 h-px bg-primary-500 inline-block"></span>
                Principles
              </p>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">内容原则</h2>
              <p className="mt-3 text-sm md:text-base text-foreground-600 leading-relaxed">
                信息越快，越需要克制。以下四条原则，是我们判断一条内容能否收录的标准。
              </p>
              <div className="mt-6 p-5 rounded-lg bg-secondary-50 border border-secondary-200">
                <h3 className="font-heading text-base font-semibold text-foreground-900">发展理念</h3>
                <p className="mt-2 text-sm text-foreground-600 leading-relaxed">
                  先做减法再做加法。减少情绪化表达，增加可核验的事实，让每一次浏览都带回一些真实的信息。
                </p>
              </div>
            </div>
            <div className="lg:col-span-7">
              <ul className="space-y-4">
                {principles.map((item, index) => (
                  <li key={item.title} className="flex items-start gap-4 p-5 rounded-lg bg-background-50 border border-background-200">
                    <span className="w-9 h-9 shrink-0 rounded-md bg-accent-500 text-foreground-950 flex items-center justify-center font-heading font-bold">
                      {index + 1}
                    </span>
                    <div>
                      <h3 className="font-heading text-base font-semibold text-foreground-900">{item.title}</h3>
                      <p className="mt-1.5 text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        <section className="w-full bg-background-100 border-y border-background-200">
          <div className="w-full px-4 md:px-8 py-14 md:py-20">
            <div className="max-w-2xl">
              <p className="inline-flex items-center gap-2 text-xs font-label tracking-[0.2em] uppercase text-primary-600 mb-3">
                <span className="w-6 h-px bg-primary-500 inline-block"></span>
                Voices
              </p>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">读者口碑</h2>
            </div>
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {testimonials.map((item) => (
                <figure key={item.name} className="flex flex-col bg-background-50 border border-background-200 rounded-lg p-5">
                  <i className="ri-double-quotes-l text-2xl text-accent-500" aria-hidden="true"></i>
                  <blockquote className="mt-3 text-sm text-foreground-700 leading-relaxed flex-1">{item.text}</blockquote>
                  <figcaption className="mt-5 flex items-center gap-3 pt-4 border-t border-background-200">
                    <span className="w-10 h-10 rounded-full overflow-hidden bg-background-200 shrink-0">
                      <img
                        src={item.avatar}
                        alt={`${item.name} 的头像`}
                        title={`${item.name} - 读者口碑`}
                        className="w-full h-full object-cover"
                      />
                    </span>
                    <span className="min-w-0">
                      <span className="block text-sm font-medium text-foreground-900 truncate">{item.name}</span>
                      <span className="block text-xs text-foreground-500 truncate">{item.role}</span>
                    </span>
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        </section>

        <FaqSection
          id="about-faq"
          title="关于本站的疑问"
          desc="关于内容定位、核验流程与合作方式的常见问题，集中在这里解答。"
        />

        <section className="w-full px-4 md:px-8 py-14 md:py-20">
          <div className="rounded-lg bg-primary-950 text-background-100 p-8 md:p-12">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
              <div className="lg:col-span-7">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-background-50">联系我们</h2>
                <p className="mt-3 text-sm md:text-base text-background-300 leading-relaxed">
                  投稿线索、内容更正、媒体合作，都欢迎来信。请在邮件中说明来意与相关链接，方便我们快速处理。
                </p>
                <div className="mt-6 flex flex-wrap gap-4">
                  <span className="inline-flex items-center gap-2 px-4 py-3 rounded-md bg-background-50/10 border border-background-50/15 text-sm text-background-100">
                    <i className="ri-mail-line text-accent-400" aria-hidden="true"></i>
                    {siteInfo.email}
                  </span>
                  <span className="inline-flex items-center gap-2 px-4 py-3 rounded-md bg-background-50/10 border border-background-50/15 text-sm text-background-100">
                    <i className="ri-weibo-line text-accent-400" aria-hidden="true"></i>
                    {siteInfo.weibo}
                  </span>
                </div>
              </div>
              <div className="lg:col-span-5">
                <div className="rounded-lg bg-background-50/10 border border-background-50/15 p-6">
                  <h3 className="font-heading text-lg font-semibold text-background-50">订阅热瓜更新</h3>
                  <p className="mt-2 text-sm text-background-300 leading-relaxed">
                    留下邮箱，有新瓜料与热榜变化时第一时间通知你。
                  </p>
                  <a
                    href="#subscribe"
                    className="mt-5 inline-flex items-center gap-2 px-5 py-3 rounded-md bg-accent-500 text-foreground-950 text-sm font-medium hover:bg-accent-400 transition-colors whitespace-nowrap"
                  >
                    <i className="ri-notification-3-line" aria-hidden="true"></i>
                    立即订阅
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
      <BackToTop />
    </div>
  );
}